import {
  BarChart3, Package, UserSearch, Settings, Eye, Warehouse, ClipboardList, Banknote, Tag,
  ShieldBan, QrCode, TicketPercent, ClipboardCheck, PackageSearch, PieChart, StickyNote,
  RotateCcw, PackageX, MessageSquarePlus, Handshake, Presentation, ScanSearch, Combine,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type StoreType = "woocommerce" | "wholesale";

export type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  adminOnly?: boolean;
  /** `adminOnly` stavka koju vide i korisnici s dodatnom rolom `dev`. */
  devToo?: boolean;
  /** Extra search terms for the ⌘K palette — old names, English equivalents, common typos. */
  keywords?: string[];
  /** Which badge count to render, if any. */
  badge?: "newReturns";
  /** Which store types show this item. Defaults to WooCommerce only. */
  storeTypes?: StoreType[];
};

export type NavCtx = { isAdmin: boolean; isDev?: boolean; storeType: StoreType };

const BOTH: StoreType[] = ["woocommerce", "wholesale"];

export type NavGroup = {
  id: string;
  /** Empty label renders the group as a bare block at the top — no heading, never collapsible. */
  label: string;
  items: NavItem[];
};

/**
 * Single source of truth for navigation. The sidebar renders these as collapsible
 * groups; the ⌘K palette searches across all of them.
 */
export const navGroups: NavGroup[] = [
  {
    id: "veleprodaja",
    label: "",
    items: [
      { to: "/wholesale/orders", label: "Narudžbe", icon: Package, storeTypes: ["wholesale"], keywords: ["veleprodaja", "narudzbe", "wholesale"] },
      { to: "/wholesale/analytics", label: "Analitika", icon: PieChart, storeTypes: ["wholesale"], keywords: ["analitika", "statistika", "izvjestaj", "najprodavaniji", "sell-through", "kupci"] },
      { to: "/wholesale/partners", label: "Partneri", icon: Handshake, storeTypes: ["wholesale"], keywords: ["partneri", "kupci", "butici"] },
      { to: "/wholesale/catalog", label: "Katalog", icon: Tag, storeTypes: ["wholesale"], keywords: ["katalog", "artikli", "proizvodi"] },
      { to: "/wholesale/prezentacija", label: "Prezentacija", icon: Presentation, storeTypes: ["wholesale"], keywords: ["prezentacija", "izlog", "showroom", "komercijalista", "teren"] },
      { to: "/wholesale/settings", label: "Postavke veleprodaje", icon: Settings, storeTypes: ["wholesale"], adminOnly: true, keywords: ["shopovi", "pdv"] },
    ],
  },
  {
    // Daily drivers, pulled out of their domain groups so they need no scrolling
    // and no expanding. Deliberately unlabeled — a heading here would just be noise.
    id: "glavno",
    label: "",
    items: [
      { to: "/orders", label: "Narudžbe", icon: Package, keywords: ["orders", "narudzbe", "porudzbine"] },
      { to: "/products", label: "Proizvodi", icon: Tag, keywords: ["products", "proizvodi", "artikli"] },
      { to: "/dashboard", label: "Pregled", icon: BarChart3, adminOnly: true, keywords: ["dashboard", "pocetna"] },
      { to: "/returns", label: "Povrati", icon: RotateCcw, badge: "newReturns", keywords: ["returns", "povrati", "reklamacije"] },
    ],
  },
  {
    id: "prodaja",
    label: "Prodaja",
    items: [
      { to: "/customers", label: "Kupci", icon: UserSearch, keywords: ["customers", "klijenti"] },
      { to: "/coupons", label: "Kuponi", icon: TicketPercent, keywords: ["coupons", "popusti"] },
      { to: "/analytics", label: "Analitika", icon: PieChart, adminOnly: true, keywords: ["analytics", "statistika", "izvjestaji"] },
      { to: "/banned-customers", label: "Banovani kupci", icon: ShieldBan, adminOnly: true, keywords: ["ban", "blokirani"] },
    ],
  },
  {
    id: "otprema",
    label: "Otprema",
    items: [
      { to: "/fulfillment/merge", label: "Spajanje pošiljki", icon: Combine, keywords: ["spajanje", "meta", "spoji", "posiljke", "isti kupac"] },
      { to: "/fulfillment/labels", label: "Naljepnice", icon: StickyNote, storeTypes: BOTH, keywords: ["labels", "naljepnice", "etikete"] },
      { to: "/fulfillment/pick", label: "Priprema", icon: ClipboardList, storeTypes: BOTH, keywords: ["pick", "picking", "priprema"] },
      { to: "/fulfillment/pack", label: "Pakovanje", icon: Package, storeTypes: BOTH, keywords: ["pack", "packing", "pakovanje"] },
      { to: "/fulfillment/cod", label: "Otkupnine", icon: Banknote, keywords: ["cod", "pouzece", "otkupnina"] },
      { to: "/fulfillment/returned", label: "Vraćeni paketi", icon: PackageX, keywords: ["vraceni", "returned", "paketi"] },
    ],
  },
  {
    id: "skladiste",
    label: "Skladište",
    items: [
      { to: "/provjera-stanja", label: "Provjera stanja", icon: ScanSearch, keywords: ["stanje", "zaliha", "zalihe", "provjera", "skener", "sku", "stock"] },
      { to: "/warehouse/scan", label: "Skeniranje", icon: Warehouse, keywords: ["scan", "skladiste", "skener", "warehouse"] },
      { to: "/warehouse/brojanje-robe", label: "Fakturno brojanje", icon: ClipboardCheck, keywords: ["brojanje", "fakture", "roba", "ocr"] },
      { to: "/warehouse/brojanje-posiljki", label: "Brojanje pošiljki", icon: PackageSearch, keywords: ["brojanje", "posiljke", "posiljki"] },
      { to: "/skugen", label: "SKU Gen", icon: QrCode, keywords: ["sku", "barkod", "generator"] },
    ],
  },
];

/** Rendered in the sidebar footer, outside the main groups — tools, not daily workflow. */
export const footerItems: NavItem[] = [
  { to: "/bugovi", label: "Prijave", icon: MessageSquarePlus, adminOnly: true, devToo: true, keywords: ["bug", "bugovi", "greske", "prijave", "prijedlozi", "ideje", "feature"] },
  { to: "/god-view", label: "God View", icon: Eye, adminOnly: true, keywords: ["god", "view", "pregled svega"] },
  { to: "/settings", label: "Postavke", icon: Settings, storeTypes: BOTH, keywords: ["settings", "postavke", "podesavanja"] },
];

const forType = (item: NavItem, t: StoreType) => (item.storeTypes ?? ["woocommerce"]).includes(t);

const allowed = (item: NavItem, ctx: NavCtx) =>
  !item.adminOnly || ctx.isAdmin || (!!item.devToo && !!ctx.isDev);

export function visibleItems(items: NavItem[], ctx: NavCtx): NavItem[] {
  return items.filter((item) => allowed(item, ctx) && forType(item, ctx.storeType));
}

/** All navigable items, flattened — used by the ⌘K palette. */
export function allNavItems(ctx: NavCtx): NavItem[] {
  return visibleItems([...navGroups.flatMap((g) => g.items), ...footerItems], ctx);
}

/** Lowercase and strip Bosnian diacritics so "narudzbe" matches "Narudžbe". */
export function normalize(s: string): string {
  return s
    .toLowerCase()
    .replace(/đ/g, "d")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function matchesQuery(item: NavItem, query: string): boolean {
  const q = normalize(query.trim());
  if (!q) return true;
  const haystack = [item.label, ...(item.keywords ?? [])].map(normalize);
  return haystack.some((h) => h.includes(q));
}
